import { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';

/**
 * Tabs Component (Preline UI)
 *
 * A reusable tabs component using Preline UI styling and functionality.
 * Provides accessible tab navigation with automatic initialization.
 * Preline's JavaScript handles tab switching and active states.
 *
 * @param {Object} props
 * @param {Array} props.tabs - Array of tab objects [{id: string, label: string, content: React.Node}]
 * @param {number} [props.defaultTab=0] - Index of the default active tab
 * @param {string} [props.variant='default'] - Visual variant ('default', 'pills', 'underline', 'segment')
 */
export function Tabs({ tabs, defaultTab = 0, variant = 'default' }) {
  const tabsRef = useRef(null);

  // Initialize Preline tabs on mount and when tabs change
  useEffect(() => {
    if (tabsRef.current && window.HSStaticMethods) {
      window.HSStaticMethods.autoInit(['tabs']);
    }
  }, [tabs]);

  // Base classes for tab navigation
  const navClasses = {
    default: 'border-b border-border-default flex gap-x-1',
    pills: 'inline-flex space-x-2',
    underline: 'border-b border-border-default flex gap-x-6',
    segment: 'inline-flex bg-brand/5 hover:bg-brand/6 rounded-lg p-1',
  };

  // Base classes for individual tabs
  const tabClasses = {
    default: `
      hs-tab-active:font-semibold hs-tab-active:border-primary hs-tab-active:text-primary
      py-4 px-1 inline-flex items-center gap-x-2 border-b-2 border-transparent text-sm
      whitespace-nowrap text-muted hover:text-primary focus:outline-none focus:text-primary
      disabled:opacity-50 disabled:pointer-events-none cursor-pointer transition-all
    `,
    pills: `
      hs-tab-active:bg-primary hs-tab-active:text-white
      py-3 px-4 inline-flex items-center gap-x-2 bg-transparent text-sm text-muted
      hover:bg-hover-muted focus:outline-none rounded-lg disabled:opacity-50
      disabled:pointer-events-none cursor-pointer transition-all
    `,
    underline: `
      hs-tab-active:font-semibold hs-tab-active:text-primary hs-tab-active:border-b-primary
      py-3 inline-flex items-center gap-x-2 border-b-2 border-transparent text-sm
      whitespace-nowrap text-muted hover:text-primary focus:outline-none
      disabled:opacity-50 disabled:pointer-events-none cursor-pointer transition-all
    `,
    segment: `
      hs-tab-active:bg-app hs-tab-active:text-default hs-tab-active:shadow-sm
      py-2 px-4 inline-flex items-center gap-x-2 bg-transparent text-sm text-muted
      hover:text-default font-medium rounded-md disabled:opacity-50
      disabled:pointer-events-none cursor-pointer transition-all
    `,
  };

  return (
    <div ref={tabsRef}>
      {/* Tab Navigation */}
      <nav className={navClasses[variant]} role="tablist" aria-label="Tabs">
        {tabs.map((tab, index) => (
          <button
            key={tab.id}
            type="button"
            className={`${tabClasses[variant]} ${index === defaultTab ? 'active' : ''}`}
            id={`${tab.id}-tab`}
            data-hs-tab={`#${tab.id}`}
            aria-controls={tab.id}
            role="tab"
            aria-selected={index === defaultTab}
          >
            {tab.label}
          </button>
        ))}
      </nav>

      {/* Tab Panels */}
      <div className="mt-6">
        {tabs.map((tab, index) => (
          <div
            key={tab.id}
            id={tab.id}
            role="tabpanel"
            aria-labelledby={`${tab.id}-tab`}
            className={index === defaultTab ? '' : 'hidden'}
          >
            {tab.content}
          </div>
        ))}
      </div>
    </div>
  );
}

Tabs.propTypes = {
  tabs: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      content: PropTypes.node.isRequired,
    })
  ).isRequired,
  defaultTab: PropTypes.number,
  variant: PropTypes.oneOf(['default', 'pills', 'underline', 'segment']),
};
